/**
 * openExternal — open a URL in the user's actual system browser, not the PWA shell.
 *
 * Why this util exists:
 *   When the app is installed as a PWA (added to homescreen), `window.open(url, '_blank')`
 *   opens the URL in the PWA's internal WKWebView (iOS) or Custom Tab (Android) instead
 *   of Safari / Chrome. From a user perspective the source article looks like it's stuck
 *   inside the app — they can't share it, can't bookmark it, can't multi-tab it.
 *
 *   The reliable cross-platform workaround is to synthesise a real `<a target="_blank">`
 *   element, append it briefly to the DOM, and trigger a `.click()`. Browsers treat that
 *   as a user-initiated navigation and route it to the system browser even in standalone
 *   mode. Falls back to `window.open` for any environment where the trick fails.
 *
 * Usage:
 *   import openExternal from '@/utils/openExternal';
 *   openExternal('https://www.bbc.co.uk/news/article-123');
 *
 *   // or as an onClick handler
 *   onClick={(e) => { e.stopPropagation(); openExternal(sourceUrl); }}
 */
export default function openExternal(url) {
  if (!url) return;

  try {
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.rel = 'noopener noreferrer external';
    // 'external' rel hint encourages iOS standalone PWAs to break out to Safari.
    a.style.position = 'fixed';
    a.style.left = '-9999px';
    document.body.appendChild(a);
    a.click();
    // Clean up after the click has fired.
    setTimeout(() => {
      try { document.body.removeChild(a); } catch { /* already gone */ }
    }, 0);
  } catch {
    // Last-resort fallback — programmatic open. Less reliable in standalone PWAs
    // but still correct in regular browser tabs.
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}
